import useQuery from '../../../../ReactQuery/useQuery';
import type { VcsRepository } from '../../../../../shared/types/routes/vcs/githubApp';
import type { VcsApp } from '../../../../../shared/vcs/githubApp/apps';
import type { QueryResponse } from '../../../../ReactQuery/useQuery';

import { fetchGitHubAppRepositories, getGitHubAppRepositoriesQueryKeyPrefix } from './useGitHubAppRepositories';

// The server caps pageSize at 100, so enumerate with the largest page it allows.
const ENUMERATION_PAGE_SIZE = 100;

// Recursive like legacy ModalBulkImport's loadRepoPage: one page at a time, sequentially.
type FetchAllGitHubAppRepositoriesArgs = {
  app: VcsApp;
  connectionId: string;
  q: string;
  excludeBranches?: boolean;
  page?: number;
  accumulated?: VcsRepository[];
};

const fetchAllGitHubAppRepositories = async ({
  app,
  connectionId,
  q,
  excludeBranches,
  page = 1,
  accumulated = [],
}: FetchAllGitHubAppRepositoriesArgs): Promise<VcsRepository[]> => {
  const response = await fetchGitHubAppRepositories(app, {
    page,
    pageSize: ENUMERATION_PAGE_SIZE,
    q,
    connectionId,
    excludeBranches,
  });
  const body = response.data;
  const results = body?.results ?? [];
  const repositories = accumulated.concat(results);
  // The empty-page check guards against recursing forever on a server that keeps
  // reporting more pages without returning rows.
  if (!body?.hasMore || results.length === 0) {
    return repositories;
  }
  return fetchAllGitHubAppRepositories({
    app,
    connectionId,
    q,
    excludeBranches,
    page: page + 1,
    accumulated: repositories,
  });
};

type UseAllGitHubAppRepositoriesOptions = {
  /** Gate the enumeration: pass false until the caller actually needs the list (e.g. modal closed). */
  enabled?: boolean;
  /**
   * Name search to scope the enumeration to, matching the listing's `q`. Import-all ignores the
   * page's search box (like the legacy bulk-import modal), so it leaves this empty.
   */
  q?: string;
  /**
   * Skip the per-repo branch listing (one GitHub call each). "Select all" only needs the default
   * branch, so it sets this; import-all leaves it off and keeps the full list.
   */
  excludeBranches?: boolean;
};

/**
 * Enumerates every repository of one connected account by paging the listing endpoint
 * client-side until the server reports no more pages. Feeds the Import All wizard's review
 * step (count, filters, preview); the filtered list from this enumeration is what the wizard
 * submits to the import endpoint (there is no server-side re-enumeration). Not cached:
 * each enable (modal open) runs a fresh enumeration, matching the legacy modal's
 * reload-per-open, and a failed run surfaces the legacy-style error card (no auto retry,
 * since every page is an expensive server-side call).
 */
export function useAllGitHubAppRepositories(
  app: VcsApp,
  connectionId: string,
  { enabled = true, q = '', excludeBranches }: UseAllGitHubAppRepositoriesOptions = {},
) {
  return useQuery({
    queryKey: [...getGitHubAppRepositoriesQueryKeyPrefix(app), 'all', connectionId, q, excludeBranches] as const,
    queryFn: async () =>
      ({
        data: await fetchAllGitHubAppRepositories({ app, connectionId, q, excludeBranches }),
      }) as QueryResponse<VcsRepository[]>,
    enabled,
    staleTime: 0,
    cacheTime: 0,
    retry: false,
  });
}
